import React, { FC, useEffect } from "react"
import { useMutation } from "@apollo/client/react"
import { Modal } from "@bancacfplus/Modal"
import SubmitButton from "@bancacfplus/SubmitButton"
import { useUserFirstAccessPoste } from "./hooks/useUserFirstAccessPoste"
import { ErrorBoundaryEnriched } from "../ErrorBoundaryEnriched"
import { setUserBehavioursMutation } from "../../queries/setUserBehaviours"
import { getDocsByClientSource, Docs as DocsLinks } from "../../utils/getDocsByClientSource"
import { setUserBehaviours } from "../../queries/__generated-typings__/setUserBehaviours"
import style from "./FirstAccessPoste.module.scss"
import Checkbox from "@bancacfplus/Checkbox"
import { loadEnvVars } from "../../utils/loadEnvVars"
import { useForm } from "react-hook-form"

type Form = { transparency_documentation: boolean; terms_conditions: boolean }
type DocsNames = { name: string; key: string }[]

const { expressApiGateway } = loadEnvVars()
const docsNames: DocsNames = [
  {
    name: "Guida ABF",
    key: "guidaABF"
  },
  {
    name: "Foglio informativo Digital Lending",
    key: "foglioInformativo"
  },
  {
    name: "Contratto di finanziamento Digital Lending Fac Simile",
    key: "facsimile"
  },
  {
    name: "Guida alla Centrale Rischi",
    key: "centraleRischi"
  },
  {
    name: "Rilevazioni Trimestrali Antiusura",
    key: "tegmAntiusura"
  },
  {
    name: "Informativa Privacy Banca CF+",
    key: "informativaPrivacy"
  }
]

async function automaticDownload(docUrls: DocsLinks) {
  Object.values(docUrls).forEach(async url => {
    try {
      const response = await fetch(`${expressApiGateway}/download-static-file?url=${encodeURIComponent(url)}`)

      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.statusText}`)
      }

      const blob = await response.blob()
      const link = document.createElement("a")
      const urlBlob = URL.createObjectURL(blob)

      link.href = urlBlob
      link.download = url.split("/").pop() as string // Extract filename from the URL
      document.body.appendChild(link)
      link.click()

      // Cleanup - Free up memory
      document.body.removeChild(link)
      URL.revokeObjectURL(urlBlob)
    } catch (error) {
      console.error("Error downloading document:", error)
    }
  })
}

export const FirstAccessPoste: FC = () => {
  const { data: userData, error, loading } = useUserFirstAccessPoste()
  const [setUserDocs, { data: resultSetUserDocsMutation, loading: loadingMutation, error: errorMutation }] =
    useMutation<setUserBehaviours>(setUserBehavioursMutation)
  const docLinks = getDocsByClientSource("poste")

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<Form>({
    reValidateMode: "onSubmit"
  })
  useEffect(() => {
    if (resultSetUserDocsMutation) automaticDownload(docLinks)
  }, [resultSetUserDocsMutation, docLinks])

  if (loading || loadingMutation) {
    return <></>
  }

  if (error || errorMutation) {
    return (
      <Modal close="dark" hash={false} type="cta" opened={true} className={style.modal} id="first-access-poste-error">
        <ErrorBoundaryEnriched error={error || errorMutation} swallow />
      </Modal>
    )
  }

  const onSubmit = () => {
    setUserDocs({
      variables: {
        eventLabel: "partner_api_transparency_documentation_and_terms_conditions",
        eventValue: "true",
        user_id: userData.id
      }
    })
  }

  const transparencyRegister = register("transparency_documentation", { required: true })
  const termsRegister = register("terms_conditions", { required: true })

  return (
    <>
      {userData.askToReadDocs && !resultSetUserDocsMutation && (
        <Modal close="dark" hash={false} type="cta" opened={true} className={style.modal} id="first-access-poste">
          <h4 className={style.modalTitle}>Benvenuto/a in Banca CF+</h4>
          <p className={style.modalText}>
            Prima di proseguire ti preghiamo di prendere visione della seguente documentazione
          </p>
          <form className={style.modalForm} noValidate onSubmit={handleSubmit(onSubmit)}>
            <h5 className={style.modalSubTitle}>Documentazione di trasparenza</h5>
            <fieldset name="transparency_docs" className={style.modalContent}>
              <legend id="privacy-policy-poste" className={style.screenReaderOnly}>
                Informativa Privacy e Contratto di piattaforma
              </legend>
              <div className={style.documentsLinks}>
                {docsNames.map((doc, index) => (
                  <a key={index} href={docLinks[doc.key]} target="_blank" rel="noreferrer">
                    {doc.name}
                  </a>
                ))}
              </div>

              <Checkbox
                propsRef={transparencyRegister.ref as unknown as React.RefObject<HTMLInputElement>}
                input={{ ...transparencyRegister }}
                name="transparency_documentation"
                text={<p>Ho preso visione della documentazione di trasparenza.</p>}
                error="Questo campo è obbligatorio"
                invalid={!!errors.transparency_documentation}
                className={style.checkbox}
              />
            </fieldset>

            <h5 className={style.modalSubTitle}>Contratto di termini e condizioni</h5>

            <fieldset name="contract_terms_conditions" className={style.modalContent}>
              <legend id="terms-and-conditions-poste" className={style.screenReaderOnly}>
                Contratto di termini e condizioni
              </legend>
              <Checkbox
                propsRef={termsRegister.ref as unknown as React.RefObject<HTMLInputElement>}
                input={{ ...termsRegister }}
                name="terms_conditions"
                text={
                  <p>
                    Ho preso visione del{" "}
                    <a href={docLinks.termsAndConditions} target="_blank" rel="noreferrer">
                      contratto di termini e condizioni
                    </a>{" "}
                    per l&apos;utilizzo della Piattaforma e le relative responsabilità.
                  </p>
                }
                error="Questo campo è obbligatorio"
                invalid={!!errors.terms_conditions}
                className={style.checkbox}
              />
            </fieldset>

            <p className={style.modalText}>
              Cliccando su &quot;Prosegui&quot; scaricherai la documentazione di trasparenza di cui sopra.
            </p>
            <SubmitButton type="submit">Prosegui</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  )
}
